import { encryptLoginPassword } from '@tiger/api-signature';
import { observer } from 'mobx-react';
import React, { useRef, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import { useForm, withForm } from 'react-bootstrap-formutil';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import Button from 'components/base/Button';
import GeeCaptcha, { CaptchaType } from 'components/Captcha';
import Debug from 'components/Debug';
import FormGroup from 'components/FormGroup';
import Email from 'components/forms/Email';
import TelPhone from 'components/forms/TelcodesPhone';
import Toast from 'components/Toast';
import security from 'stores/security';
import { RegisterTypeEnum } from 'types/common';

const Login: React.FC = () => {
    const [captcha, setCaptcha] = useState<boolean>(false);
    const captchaRef = useRef<any>();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { $errors, $invalid, $params, $getFirstError, $batchState } = useForm();
    const loginType = searchParams.get('loginType') || RegisterTypeEnum.PHONE;

    const onSubmit = async (ev?: React.FormEvent) => {
        ev?.preventDefault();

        login();
    };

    const login = async (extraParams = {}) => {
        if ($invalid) {
            $batchState({
                $invalid: true,
                $dirty: true
            });

            Toast.show($getFirstError());

            if ($errors.validate_code) {
                captchaRef.current!.trigger();
            }
        } else {
            Toast.loading(true);

            try {
                const params = {
                    ...$params,
                    ...extraParams,
                    grant_type: loginType,
                    login_password: encryptLoginPassword($params.login_password),
                    pwd_api_ver: 'v2'
                };

                await security.login(params);

                await security.getStatus();
            } catch (error) {
                Toast.show(error);

                const needCaptcha =
                    captcha ||
                    (error.response &&
                        error.response.data &&
                        error.response.data.data &&
                        error.response.data.data.extra &&
                        error.response.data.data.extra.captcha) ||
                    (error.error_code >= 11011 && error.error_code <= 11015); // 验证码为空

                if (needCaptcha) {
                    setCaptcha(needCaptcha);
                }

                // 如果验证码框已经出现，就刷新验证码
                // 这里统一处理成只要请求出错，就刷新一次验证码
                if (captchaRef.current) {
                    captchaRef.current.reset();

                    if (captchaRef.current.captchaType === CaptchaType.RECAPTCHA_SCORE) {
                        setCaptcha(false);
                    }
                }
            }

            Toast.loading(false);
        }
    };

    if (security.isLogin) {
        const next = searchParams.get('next') || '/profile';

        if (next.startsWith('/accounts')) {
            return <Navigate to={next.replace('/accounts', '')} />;
        }

        window.location.href = next;

        return null;
    }

    const getNewQueryString = (loginType: RegisterTypeEnum) => {
        const search = new URLSearchParams(location.search);

        search.set('loginType', loginType);

        return search.toString();
    };

    return (
        <form noValidate onSubmit={onSubmit}>
            <h3 className="open-title">{__('login')}</h3>
            {loginType === RegisterTypeEnum.EMAIL ? (
                <Email name="username" />
            ) : (
                <TelPhone
                    phoneOptions={{
                        name: 'username'
                    }}
                />
            )}
            <FormGroup
                name="login_password"
                required
                label={__('Password')}
                errorLevel="off"
                validMessage={{ required: __('Please enter your password') }}>
                <FormControl type="password" />
            </FormGroup>

            {captcha && (
                <div className="form-group">
                    <GeeCaptcha action="signin" ref={captchaRef} onSuccess={onSubmit} />
                </div>
            )}
            <Button className="mb-2" block type="primary" size="lg" htmlType="submit">
                {__('Login')}
            </Button>
            <div className="other-login-type">
                {loginType === RegisterTypeEnum.EMAIL ? (
                    <Link
                        to={{
                            pathname: '/login',
                            search: getNewQueryString(RegisterTypeEnum.PHONE)
                        }}>
                        {__('Login with Phone')}
                    </Link>
                ) : (
                    <Link
                        to={{
                            pathname: '/login',
                            search: getNewQueryString(RegisterTypeEnum.EMAIL)
                        }}>
                        {__('Login with Email')}
                    </Link>
                )}
            </div>
            <Debug data={$params} />
        </form>
    );
};

export default withForm(observer(Login));
